import { Trash2, Plus, Link, AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export interface GrupoMember {
  id: number;
  name: string;
  cnpj: string;
  status: "active" | "inactive";
  isMatriz?: boolean;
  matrizId?: number | null;
  cidade?: string;
  estado?: string;
  employeesCount?: number;
}

interface GrupoMemberListSectionProps {
  members?: GrupoMember[];
  isLoading?: boolean;
  isError?: boolean;
  isPermissionError?: boolean;
  onRetry?: () => void;
  onCreateMember?: () => void;
  onLinkExisting?: () => void;
  onUnlink?: (member: GrupoMember) => void;
  onOpen?: (member: GrupoMember) => void;
}

/**
 * Componente para listar membros de um grupo econômico (para Controladora)
 *
 * Estados suportados:
 * - Loading: Skeleton de 6 linhas com animação
 * - Error: Alert destrutivo com botão de retry
 * - Empty: Alert informativo com CTAs para criar/vincular
 * - Data: Lista de membros com ações (abrir, remover)
 */
export default function GrupoMemberListSection({
  members = [],
  isLoading = false,
  isError = false,
  isPermissionError = false,
  onRetry,
  onCreateMember,
  onLinkExisting,
  onUnlink,
  onOpen,
}: GrupoMemberListSectionProps) {
  // Skeleton de carregamento (enterprise)
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Membros do Grupo Econômico</CardTitle>
          <CardDescription>Carregando membros...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="border rounded-lg p-4 space-y-3 animate-pulse"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-24" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Erro ao carregar (enterprise)
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Membros do Grupo Econômico</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {isPermissionError
                ? "Você não tem permissão para acessar os membros deste grupo econômico."
                : "Erro ao carregar membros do grupo. Tente novamente."}
            </AlertDescription>
          </Alert>
          {!isPermissionError && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="mt-4"
            >
              Tentar Novamente
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Empty state (enterprise)
  if (members.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Membros do Grupo Econômico</CardTitle>
          <CardDescription>
            Nenhum membro cadastrado neste grupo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Você ainda não tem membros neste grupo econômico. Clique nos botões abaixo para adicionar membros.
            </AlertDescription>
          </Alert>
          <div className="flex gap-2 mt-4">
            {onCreateMember && (
              <Button
                onClick={onCreateMember}
                className="flex-1"
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Empresa
              </Button>
            )}
            {onLinkExisting && (
              <Button
                onClick={onLinkExisting}
                variant="outline"
                className="flex-1"
              >
                <Link className="h-4 w-4 mr-2" />
                Vincular Existente
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Listar membros (data state)
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Membros do Grupo Econômico</CardTitle>
            <CardDescription>
              {members.length} membro{members.length !== 1 ? "s" : ""} cadastrado{members.length !== 1 ? "s" : ""}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {onCreateMember && (
              <Button
                onClick={onCreateMember}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar
              </Button>
            )}
            {onLinkExisting && (
              <Button
                onClick={onLinkExisting}
                variant="outline"
                size="sm"
              >
                <Link className="h-4 w-4 mr-2" />
                Vincular
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {members.map((member) => (
            <div
              key={member.id}
              className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-base mb-1">
                    {member.name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    CNPJ: {member.cnpj}
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  {member.isMatriz && (
                    <Badge variant="outline">Matriz</Badge>
                  )}
                  {member.matrizId && (
                    <Badge variant="secondary">Filial (herdada)</Badge>
                  )}
                  <Badge
                    variant={member.status === "active" ? "default" : "secondary"}
                  >
                    {member.status === "active" ? "Ativa" : "Inativa"}
                  </Badge>
                </div>
              </div>

              {(member.cidade || member.employeesCount !== undefined) && (
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  {member.cidade && (
                    <div className="text-muted-foreground">
                      {member.cidade}, {member.estado}
                    </div>
                  )}
                  {member.employeesCount !== undefined && (
                    <div className="text-muted-foreground">
                      {member.employeesCount} colaboradores
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                {onOpen && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpen(member)}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Abrir
                  </Button>
                )}
                {onUnlink && !member.matrizId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => onUnlink(member)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remover
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
